/**
 * The King.Prostor CAD views (0584; folder pages 2026-09-16; card landing + comma-split
 * labels 2026-09-28):
 *
 * - the overview (`…/docs/cad`): crumbs, the title, one line (»61 map · 420 slojev«) and a
 *   card grid — one card per folder page in tree order (eyebrow = the folders above it,
 *   title = the folder's name before the first comma, the publisher after it as a muted
 *   line, its layer count, the first layer names), then one card per »Rastri in LiDAR«
 *   product. No per-include sections, no pills, no rail: the sidebar tree is the index
 *   (Teo: the old overview was 5,354 px of the sidebar repeated).
 *
 * - a folder page (`…/docs/cad/<slugs…>`): one page per folder that holds layers (Teo:
 *   "each lowest level folder [has] a page, with layers all on 1 page listed") — the
 *   folder's name split at its first comma (the publisher as a muted line under the h1),
 *   its text, then every layer as a section with its full facts card (`#sloj-<xml_id>`,
 *   renderSection.ts — the same section the web theme pages use) with its chip, product
 *   link, text, date rows and, for a king admin, the edit button / form / history. The
 *   rail lists the layers; the pager walks the pages in tree order.
 */

import type { WikiCadLayerNode, WikiEntry } from '../../../shared/types/layerWiki.js';
import { docsCopy } from '../docsCopy.js';
import type { EditState } from '../docsEdit.js';
import { esc, paragraphs } from '../docsHtml.js';
import {
  cadFolderLabel,
  cadLandingCards,
  cadPageNeighbours,
  cadPagePath,
  type CadPage,
  type DocsCatalogue,
} from '../docsModel.js';
import { cadLayerAnchorId, docsPath } from '../docsRouter.js';
import { renderCards } from './renderCards.js';
import { renderCadNav } from './renderKatalog.js';
import { renderEntrySection, renderMissingSection, renderPager } from './renderSection.js';

export interface CadState {
  query: string;
  navOpen: boolean;
  openFolders: ReadonlySet<string>;
}

export interface CadFolderState extends CadState {
  /** King-admin editing (0569); absent or `isKingAdmin: false` renders the public page. */
  edit?: EditState;
}

/** A page's pill label: the folder's label (its publisher split off), then its layer count. */
function pageLabel(cat: DocsCatalogue, p: CadPage): string {
  return `${cadFolderLabel(cat, p.folder).label} · ${p.layers.length}`;
}

// ---------------------------------------------------------------------------
// Overview (card landing)
// ---------------------------------------------------------------------------

export function renderCadOverview(cat: DocsCatalogue, state: CadState): string {
  const c = docsCopy.cad;
  const tree = cat.cadTree;
  const article = `
    <article class="docs-article docs-article--landing">
      <div class="docs-crumbs">
        <a class="docs-crumb-link" href="${esc(docsPath({ view: 'pregled' }))}" data-docs-link>${esc(docsCopy.katalog.crumbRoot)}</a>
        <span aria-hidden="true">/</span><span class="docs-crumb-theme">${esc(c.crumb)}</span>
      </div>
      <h1 class="docs-entry-title" tabindex="-1">${esc(c.title)}</h1>
      ${
        tree
          ? `<p class="docs-landing-intro">${esc(c.intro(cat.cadPages.length, cat.counts.cadLayers))}</p>`
          : `<p class="docs-desc docs-desc--empty" role="status">${esc(c.unavailable)}</p>`
      }
      ${renderCards(cadLandingCards(cat))}
    </article>`;
  return `
    <div class="docs-katalog docs-katalog--tree">
      ${renderCadNav(cat, state, { pageId: null, key: null })}
      <div class="docs-katalog-body docs-katalog-body--wide">
        ${article}
      </div>
    </div>`;
}

// ---------------------------------------------------------------------------
// Folder page
// ---------------------------------------------------------------------------

function renderLayer(
  cat: DocsCatalogue,
  node: WikiCadLayerNode,
  e: WikiEntry | null,
  edit: EditState | undefined
): string {
  const id = cadLayerAnchorId(node.xmlId);
  // The map names the layer, the seed does not (yet): the section stands, greyed.
  if (!e) return renderMissingSection(id, node.name);
  return renderEntrySection(cat, e, { id, section: 'cad', ...(edit ? { edit } : {}) });
}

function renderCadFolderArticle(
  cat: DocsCatalogue,
  page: CadPage,
  edit: EditState | undefined
): string {
  const c = docsCopy;
  const entries = page.layers.map((n) => (n.entryKey ? (cat.byKey.get(n.entryKey) ?? null) : null));
  // Crumbs: Dokumentacija / King.Prostor CAD / every folder above the page — a link when that
  // folder is a page itself, plain text otherwise (an include folder is a container; a Czech
  // depth-0 page has nothing above it). Names read without their publisher.
  const crumbs: string[] = [
    `<a class="docs-crumb-link" href="${esc(docsPath({ view: 'pregled' }))}" data-docs-link>${esc(c.katalog.crumbRoot)}</a>`,
    `<a class="docs-crumb-link" href="${esc(docsPath({ view: 'cad' }))}" data-docs-link>${esc(c.cad.crumb)}</a>`,
  ];
  for (let i = 0; i < page.folderIds.length - 1; i += 1) {
    const id = page.folderIds[i] as string;
    const folder = cat.cadFolderById.get(id);
    const label = folder ? cadFolderLabel(cat, folder).label : (page.names[i] ?? '');
    const between = cat.cadPageById.get(id);
    crumbs.push(
      between
        ? `<a class="docs-crumb-link" href="${esc(cadPagePath(between))}" data-docs-link>${esc(label)}</a>`
        : `<span class="docs-crumb-theme">${esc(label)}</span>`
    );
  }
  const { label: title, sub } = cadFolderLabel(cat, page.folder);
  const desc = page.folder.desc
    ? `<div class="docs-desc">${paragraphs(page.folder.desc)}</div>`
    : '';
  const subPages = page.subPages
    .map((id) => cat.cadPageById.get(id))
    .filter((p): p is CadPage => p !== undefined);
  const subHtml =
    subPages.length > 0
      ? `<section id="sec-podmape" class="docs-section">
          <h2 class="docs-section-title">${esc(c.cad.folder.subfolders)}</h2>
          <ul class="docs-pills" role="list">${subPages
            .map(
              (p) =>
                `<li><a class="docs-pill" href="${esc(cadPagePath(p))}" data-docs-link title="${esc(p.folder.name)}">${esc(pageLabel(cat, p))}</a></li>`
            )
            .join('')}</ul>
        </section>`
      : '';
  const { prev, next } = cadPageNeighbours(cat, page.id);
  const end = (p: CadPage | null): { href: string; label: string } | null =>
    p ? { href: cadPagePath(p), label: cadFolderLabel(cat, p.folder).label } : null;
  return `
    <article class="docs-article docs-cad-page">
      <div class="docs-crumbs">${crumbs.join('<span aria-hidden="true">/</span>')}</div>
      <h1 class="docs-entry-title" tabindex="-1">${esc(title)}</h1>
      ${sub ? `<div class="docs-entry-sub">${esc(sub)}</div>` : ''}
      <div class="docs-cad-meta">${esc(c.cad.layersIn(page.layers.length))}</div>
      ${desc}
      ${page.layers.map((n, i) => renderLayer(cat, n, entries[i] ?? null, edit)).join('')}
      ${subHtml}
      ${renderPager(end(prev), end(next))}
    </article>`;
}

function renderCadFolderAside(page: CadPage): string {
  const c = docsCopy;
  const items = page.layers.map(
    (n) => `<a href="#${esc(cadLayerAnchorId(n.xmlId))}">${esc(n.name)}</a>`
  );
  if (page.subPages.length > 0)
    items.push(`<a href="#sec-podmape">${esc(c.cad.folder.subfolders)}</a>`);
  return `
    <aside class="docs-aside" aria-label="${esc(c.cad.folder.onThisPage)}">
      <div class="docs-aside-title">${esc(c.cad.folder.onThisPage)}</div>
      <div class="docs-rail">${items.join('')}</div>
      <div class="docs-permalink">${esc(c.katalog.permalink)}<br><code>${esc(cadPagePath(page))}</code></div>
    </aside>`;
}

function renderCadFolderNotFound(): string {
  const c = docsCopy;
  return `
    <article class="docs-article">
      <div class="docs-crumbs">
        <a class="docs-crumb-link" href="${esc(docsPath({ view: 'pregled' }))}" data-docs-link>${esc(c.katalog.crumbRoot)}</a>
        <span aria-hidden="true">/</span><span class="docs-crumb-theme">${esc(c.cad.crumb)}</span>
      </div>
      <h1 class="docs-entry-title docs-entry-title--notice" tabindex="-1">${esc(c.cad.folder.notFound)}</h1>
      <p class="docs-desc"><a href="${esc(docsPath({ view: 'cad' }))}" data-docs-link>${esc(c.cad.folder.notFoundLink)}</a></p>
    </article>`;
}

/** A folder page; `page` null renders the notice with the tree nav beside it. */
export function renderCadFolder(
  cat: DocsCatalogue,
  page: CadPage | null,
  state: CadFolderState
): string {
  return `
    <div class="docs-katalog docs-katalog--tree">
      ${renderCadNav(cat, state, { pageId: page?.id ?? null, key: null })}
      <div class="docs-katalog-body">
        ${page ? renderCadFolderArticle(cat, page, state.edit) : renderCadFolderNotFound()}
        ${page ? renderCadFolderAside(page) : '<span></span>'}
      </div>
    </div>`;
}
